import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import {
  SEARCH_ALERTS_MODAL_PARAM,
  SEARCH_QUERY_ID_PARAM,
  SMALL_SCREEN_BOTTOM_SHEET_PARAM,
} from '@xing-com/crate-jobs-constants';
import type { GetSearchAlertsQuery } from '@xing-com/crate-jobs-graphql';

import { useCreateSearchAlert } from './use-create-search-alert';
import { useSearchAlerts } from './use-search-alerts';
import { useShowSearchAlertErrorBanner } from './use-show-search-alert-error-banner';

type UseSearchParameterResult = {
  searchQueryId: string | null;
  activeParam: string | null;
  isQuerySaved: boolean;
  isModalActive: (modalTypes: string[]) => boolean;
  onClose: () => void;
  onCreate: () => void;
};

const checkIfQueryIsSaved = (
  queryId: string | null,
  searchAlerts?: GetSearchAlertsQuery
): boolean => {
  if (!searchAlerts || !queryId) {
    return false;
  }
  if (
    searchAlerts.viewer?.searchAlerts?.__typename !==
    'SearchAlertResultResponse'
  ) {
    return false;
  }

  return !!searchAlerts.viewer.searchAlerts.collection?.find(
    (alert) => alert?.queryId === queryId
  );
};

// TODO: use useToggleSearchAlert for the LI modal cases ?
export const useSearchAlert = (): UseSearchParameterResult => {
  const { loading: isLoading, data: searchAlerts } = useSearchAlerts();
  const { getSearchParameter, deleteSearchParameter } = useSearchParameter();
  const onCreateSearchAlert = useCreateSearchAlert({
    onError: useShowSearchAlertErrorBanner('CREATE'),
  });
  const activeParam = getSearchParameter(SEARCH_ALERTS_MODAL_PARAM);
  const searchQueryId = getSearchParameter(SEARCH_QUERY_ID_PARAM);
  const allFiltersView = getSearchParameter(SMALL_SCREEN_BOTTOM_SHEET_PARAM);

  const isModalActive = (modalTypes: string[]): boolean => {
    return !!activeParam && modalTypes.includes(activeParam);
  };

  const onClose = (): void => {
    deleteSearchParameter(SEARCH_ALERTS_MODAL_PARAM);
  };

  const onCreate = (): void => {
    if (!searchQueryId) {
      return;
    }

    onCreateSearchAlert({
      queryId: searchQueryId,
      onCompleted: onClose,
      propTrackActionData: {
        PropTrackAction: 'jobs_search_alert_create_confirm',
        PropActionOrigin: 'jobs_search_recent_searches',
      },
    });
  };

  const isQuerySaved =
    !isLoading && checkIfQueryIsSaved(searchQueryId, searchAlerts);

  return {
    searchQueryId,
    activeParam: allFiltersView ? null : activeParam,
    isQuerySaved,
    isModalActive,
    onClose,
    onCreate,
  };
};
